import {LOCALES, isLocaleSupported} from '../config/locales';

/**
 * Wire getStaticProps to the JSON namespaces this page imports (static imports in the page file).
 * _app requires pageProps.messages on every route; pass only the namespaces the page tree uses.
 *
 * @param {Object} messagesByNamespace - e.g. { shared, pricing }
 * @param {string} [locale]
 * @returns {{ props: { locale: string, messages: Object } }}
 */
export function pageMessagesProps(messagesByNamespace, locale = LOCALES.DEFAULT) {
  return {
    props: {
      locale,
      messages: messagesByNamespace
    }
  };
}

/**
 * Helper function to get locale from router
 * @param {Object} router - Next.js router object
 * @returns {string} The current locale
 */
export function getLocaleFromRouter(router) {
  if (router.query?.locale) {
    const {locale} = router.query;
    return isLocaleSupported(locale) ? locale : LOCALES.DEFAULT;
  }

  const {pathname} = router;
  const foundLocale = LOCALES.SUPPORTED.find(
    locale => locale !== LOCALES.DEFAULT && pathname.startsWith(`/${locale}`)
  );

  if (foundLocale) {
    return foundLocale;
  }

  return LOCALES.DEFAULT;
}

// Re-export locale utilities
export {LOCALES};

/**
 * Helper function to extract locale and path from a URL pathname
 * @param {string} pathname - The pathname (e.g., '/gr/demo' or '/demo')
 * @returns {Object} Object with locale and path (e.g., {locale: 'gr', path: '/demo'})
 */
export function extractLocaleFromPath(pathname) {
  const nonDefaultLocales = LOCALES.SUPPORTED.filter(locale => locale !== LOCALES.DEFAULT);

  const foundLocale = nonDefaultLocales.find(
    locale => pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`
  );

  if (foundLocale) {
    if (pathname.startsWith(`/${foundLocale}/`)) {
      return {
        locale: foundLocale,
        path: pathname.replace(`/${foundLocale}`, '')
      };
    }
    if (pathname === `/${foundLocale}`) {
      return {
        locale: foundLocale,
        path: '/'
      };
    }
  }

  return {
    locale: LOCALES.DEFAULT,
    path: pathname
  };
}

/**
 * Helper function to build a localized path
 * @param {string} path - The path to localize (e.g., '/free-trial')
 * @param {string} locale - The current locale
 * @returns {string} Localized path
 */
export function getLocalizedPath(path, locale) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (locale === LOCALES.DEFAULT) {
    return normalizedPath;
  }

  return `/${locale}${normalizedPath}`;
}

/**
 * Helper function for getStaticPaths in [locale] pages
 * @returns {Object} Static paths configuration
 */
export async function getStaticPathsForLocale() {
  const {getNonDefaultLocales} = await import('../config/locales');
  return {
    paths: getNonDefaultLocales(),
    fallback: false
  };
}
